import React, { useState, useEffect, useRef } from 'react';
import { useThemeMode } from '../ThemeContext';
import './NavDropdown.css';

// Separate component for dropdown items to manage hover state
const DropdownItem = ({ item, index, isLast, mode, onClose }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <a
      href={item.href}
      onClick={() => {
        onClose();
      }}
      style={{
        display: 'block',
        padding: '12px 20px',
        textDecoration: 'none',
        color: isHovered ? '#26C3C0' : (mode === 'dark' ? '#fff' : '#333'),
        backgroundColor: isHovered ? (mode === 'dark' ? 'rgba(38, 195, 192, 0.1)' : 'rgba(38, 195, 192, 0.05)') : 'transparent',
        fontSize: '14px',
        fontWeight: '400',
        transition: 'all 0.2s ease',
        borderBottom: !isLast ? `1px solid ${mode === 'dark' ? '#333' : '#f0f0f0'}` : 'none'
      }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      {item.label}
    </a>
  );
};

const NavDropdown = ({ label, items, href }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { mode } = useThemeMode();
  const dropdownRef = useRef(null);

  // Close dropdown when theme changes to prevent stale dropdowns
  useEffect(() => {
    setIsOpen(false);
    setIsHovered(false);
  }, [mode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Calculate colors based on current mode and hover state
  const linkColor = isHovered ? '#26C3C0' : (mode === 'dark' ? '#fff' : '#333');
  const arrowColor = isHovered ? '#26C3C0' : (mode === 'dark' ? '#ccc' : '#666');

  return (
    <div 
      ref={dropdownRef}
      style={{ 
        position: 'relative', 
        display: 'inline-block',
        margin: '0 9px'
      }}
    >
      <a 
        href={href} 
        onClick={(e) => {
          e.preventDefault();
          toggleDropdown();
        }}
        style={{
          textDecoration: 'none',
          color: linkColor,
          fontSize: '18px',
          fontWeight: '200',
          padding: '8px 0',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          userSelect: 'none',
          transition: 'color 0.2s ease'
        }}
        className="nav-dropdown-link"
        onMouseEnter={() => {
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
      >
        <span>{label}</span>
        <span 
          style={{
            width: '0',
            height: '0',
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: `4px solid ${arrowColor}`,
            transition: 'all 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block',
            marginLeft: '4px',
            marginTop: '1px'
          }}
        />
      </a>
      
      {isOpen && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            minWidth: '250px',
            zIndex: 9999,
            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
            border: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`,
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            padding: '8px 0',
            marginTop: '8px'
          }}
        >
          {items.map((item, index) => (
            <DropdownItem
              key={index}
              item={item}
              index={index}
              isLast={index === items.length - 1}
              mode={mode}
              onClose={() => setIsOpen(false)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NavDropdown;
